import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConversationSettings = ({ conversation, onClose, onUpdateSettings }) => {
  const [settings, setSettings] = useState({
    notifications: true,
    disappearingMessages: false,
    disappearingTimer: '24h',
    mediaAutoDownload: false,
    readReceipts: true
  });

  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const timerOptions = [
    { value: '5m', label: '5 minutes' },
    { value: '1h', label: '1 hour' },
    { value: '24h', label: '24 hours' },
    { value: '7d', label: '7 days' }
  ];

  const handleSaveSettings = () => {
    onUpdateSettings(settings);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-end lg:items-center justify-center p-0 lg:p-4">
      <div
        className="absolute inset-0 bg-background/80"
        onClick={onClose}
      />
      <div className="relative w-full lg:max-w-md bg-card rounded-t-xl lg:rounded-xl shadow-warm-2xl animate-slide-up lg:animate-scale-in max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card p-4 border-b border-border flex items-center justify-between z-10">
          <h2 className="text-lg md:text-xl font-heading font-semibold">Conversation Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="flex items-start gap-3 pb-4 border-b border-border">
            <div className="relative flex-shrink-0">
              <div
                className={`w-16 h-16 rounded-full overflow-hidden ${
                  conversation?.photoBlurred ? 'blur-md' : ''
                }`}
              >
                <img
                  src={conversation?.avatar}
                  alt={conversation?.avatarAlt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-heading font-semibold mb-1">
                {conversation?.userName}
              </h3>
              <p className="text-sm text-muted-foreground">
                {conversation?.isOnline ? 'Online' : 'Offline'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-caption font-semibold text-muted-foreground uppercase">
              Privacy Settings
            </h3>

            <Checkbox
              label="Notifications"
              description="Receive alerts for new messages"
              checked={settings?.notifications}
              onChange={(e) => setSettings({ ...settings, notifications: e?.target?.checked })}
            />

            <Checkbox
              label="Read Receipts"
              description="Let others know when you've read their messages"
              checked={settings?.readReceipts}
              onChange={(e) => setSettings({ ...settings, readReceipts: e?.target?.checked })}
            />

            <Checkbox
              label="Auto-Download Media"
              description="Automatically download shared photos and videos"
              checked={settings?.mediaAutoDownload}
              onChange={(e) => setSettings({ ...settings, mediaAutoDownload: e?.target?.checked })}
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-caption font-semibold text-muted-foreground uppercase">
              Disappearing Messages
            </h3>

            <Checkbox
              label="Enable Disappearing Messages"
              description="Messages will automatically delete after the set time"
              checked={settings?.disappearingMessages}
              onChange={(e) => setSettings({ ...settings, disappearingMessages: e?.target?.checked })}
            />

            {settings?.disappearingMessages && (
              <div className="pl-8 space-y-2">
                <label className="text-sm font-medium">Delete messages after:</label>
                <div className="grid grid-cols-2 gap-2">
                  {timerOptions?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => setSettings({ ...settings, disappearingTimer: option?.value })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                        settings?.disappearingTimer === option?.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 pt-4 border-t border-border">
            <h3 className="text-sm font-caption font-semibold text-muted-foreground uppercase">
              Actions
            </h3>

            <button
              onClick={() => setShowReportModal(true)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
            >
              <Icon name="Flag" size={18} color="var(--color-warning)" />
              <span className="text-sm font-medium">Report User</span>
            </button>

            <button
              onClick={() => setShowBlockConfirm(true)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-error/10 rounded-lg hover:bg-error/20 transition-smooth"
            >
              <Icon name="Ban" size={18} color="var(--color-error)" />
              <span className="text-sm font-medium text-error">Block User</span>
            </button>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <Button variant="default" fullWidth onClick={handleSaveSettings}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
      {showBlockConfirm && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-background/90"
            onClick={() => setShowBlockConfirm(false)}
          />
          <div className="relative w-full max-w-sm bg-card rounded-xl shadow-warm-2xl p-6 animate-scale-in">
            <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Ban" size={24} color="var(--color-error)" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-center mb-2">
              Block {conversation?.userName}?
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              They won't be able to message you or see your profile. You can unblock them later from settings.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setShowBlockConfirm(false)}>
                Cancel
              </Button>
              <Button variant="destructive" fullWidth onClick={() => {
                setShowBlockConfirm(false);
                onClose();
              }}>
                Block User
              </Button>
            </div>
          </div>
        </div>
      )}
      {showReportModal && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-background/90"
            onClick={() => setShowReportModal(false)}
          />
          <div className="relative w-full max-w-sm bg-card rounded-xl shadow-warm-2xl p-6 animate-scale-in">
            <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Flag" size={24} color="var(--color-warning)" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-center mb-2">
              Report User
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              Why are you reporting this user?
            </p>
            <div className="space-y-2 mb-6">
              {['Inappropriate content', 'Harassment', 'Spam', 'Fake profile', 'Other']?.map((reason) => (
                <button
                  key={reason}
                  className="w-full px-4 py-3 bg-muted rounded-lg text-sm font-medium hover:bg-muted/80 transition-smooth text-left"
                >
                  {reason}
                </button>
              ))}
            </div>
            <Button variant="outline" fullWidth onClick={() => setShowReportModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationSettings;